/**
 * CohortDetails — Route entry point for /cohorts/:cohortId
 *
 * Fires all cohort-related queries upfront to warm the React Query cache.
 * Child components call the same useQuery hooks and get instant cache hits.
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorAlert } from '../components/ErrorAlert';
import { useBreadcrumbs } from '../contexts';
import { buildBreadcrumbs } from '../components/BreadcrumbBuilder';
import { useCohort, useCohortParticipants, useMentors, useMentorRequests } from '../hooks/queries';
import { useProgram, useProgramAdmins } from '../hooks/queries';
import { useRole, CohortRole } from '../hooks/usePermissions';
import { useAuthStore } from '../stores/authStore';
import { useQuery } from '@tanstack/react-query';
import { activitiesApi } from '../api/activities';
import { cohortsApi } from '../api/cohorts';
import { mentorsApi } from '../api/mentors';
import { queryKeys } from '../lib/queryClient';
import { CohortAdminView } from './cohort-views/CohortAdminView';
import { CohortMentorView } from './cohort-views/CohortMentorView';
import { CohortParticipantView } from './cohort-views/CohortParticipantView';
import { CohortVisitorView } from './cohort-views/CohortVisitorView';

function CohortDetails() {
  const { cohortId } = useParams<{ cohortId: string }>();
  const { setBreadcrumbs } = useBreadcrumbs();
  const user = useAuthStore((state) => state.user);

  const { data: cohort, isLoading: cohortLoading, error: cohortError } = useCohort(cohortId);
  const { data: program } = useProgram(cohort?.programId);
  const { data: programAdmins, isLoading: adminsLoading } = useProgramAdmins(cohort?.programId);

  const role = useRole(cohort?.programId, cohort?.cohortId);
  const isAdmin = role === CohortRole.PROGRAM_ADMIN;
  const isMentor = role === CohortRole.MENTOR;
  const isParticipant = role === CohortRole.PARTICIPANT;

  // ── Warm the cache for all tab data ──────────────────────────────────────

  // Action items — role-aware, used by CohortOverviewTab.
  // Disabled only when the cohort is both completed (by date) and closed (by admin).
  // A completed-but-still-open cohort (extended work) still needs action items.
  const actionItemsEnabled = !!cohortId && !(cohort?.status === 'completed' && cohort?.isOpen === false);
  useQuery({
    queryKey: queryKeys.cohorts.actionItems(cohortId ?? ''),
    queryFn: () => cohortsApi.getActionItems(cohortId!),
    enabled: actionItemsEnabled,
    staleTime: 60 * 1000,
  });

  // Shared across all roles
  useQuery({
    queryKey: queryKeys.activities.list(cohortId ?? ''),
    queryFn: () => activitiesApi.list(cohortId!),
    enabled: !!cohortId,
  });

  useCohortParticipants(cohortId, isAdmin ? undefined : 'enrolled');
  useMentors(cohort?.mentorsEnabled ? cohortId : undefined);

  // Admin only
  useMentorRequests(isAdmin ? cohortId : undefined);

  useQuery({
    queryKey: queryKeys.cohorts.graduationRequests(cohortId ?? ''),
    queryFn: () => cohortsApi.listGraduationRequests(cohortId!),
    enabled: !!cohortId && isAdmin,
  });

  // Participant only
  useQuery({
    queryKey: queryKeys.cohorts.userCompletions(cohortId ?? '', user?.userId ?? ''),
    queryFn: () => activitiesApi.getUserCompletions(cohortId!, user!.userId),
    enabled: !!cohortId && !!user?.userId && isParticipant,
  });

  useQuery({
    queryKey: queryKeys.mentors.mine(cohortId ?? ''),
    queryFn: () => mentorsApi.getMentorForUser(cohortId!),
    enabled: !!cohortId && isParticipant,
  });

  // Mentor only
  useQuery({
    queryKey: queryKeys.mentors.mentees(cohortId ?? '', user?.userId ?? ''),
    queryFn: () => mentorsApi.getMentees(cohortId!, user!.userId),
    enabled: !!cohortId && !!user?.userId && isMentor,
  });

  useEffect(() => {
    if (cohort && program) {
      setBreadcrumbs(buildBreadcrumbs({
        programId: program.programId,
        programName: program.programName,
        cohortId: cohort.cohortId,
        cohortName: cohort.cohortName,
      }));
    }
    return () => setBreadcrumbs([]);
  }, [cohort, program, setBreadcrumbs]);

  if (cohortLoading || adminsLoading) {
    return <LoadingSpinner message="Loading cohort details..." />;
  }

  if (cohortError) {
    const msg = cohortError instanceof Error ? cohortError.message : String(cohortError);
    const isNotFound = msg.includes('not found') || msg.includes('404');
    return (
      <SpaceBetween size="m">
        <ErrorAlert error={isNotFound ? `Cohort ${cohortId} not found` : cohortError} />
        {isNotFound && <Button href="/programs" variant="primary">Browse Programs</Button>}
      </SpaceBetween>
    );
  }

  if (!cohort) {
    return (
      <SpaceBetween size="m">
        <ErrorAlert error={`Cohort ${cohortId} not found`} />
        <Button href="/programs" variant="primary">Browse Programs</Button>
      </SpaceBetween>
    );
  }

  const sharedProps = {
    cohort,
    programAdmins: programAdmins || [],
    programWikiUrl: program?.wikiUrl,
    programName: program?.programName,
    programOwnerId: program?.ownerId,
    programOwnerName: program?.ownerName,
  };

  if (role === CohortRole.PROGRAM_ADMIN) return <CohortAdminView {...sharedProps} />;
  if (role === CohortRole.MENTOR)        return <CohortMentorView {...sharedProps} />;
  if (role === CohortRole.PARTICIPANT)   return <CohortParticipantView {...sharedProps} />;
  return <CohortVisitorView {...sharedProps} />;
}

export default CohortDetails;
